import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../api/client';
import { useAuth } from '../auth';
import Sparkline from '../components/Sparkline';
import TransferOwnerDialog from '../components/TransferOwnerDialog';
import { fmtElapsed, indexerState } from '../lib/indexerState';
import type { Archive, ArchiveTag, SparklinePoint } from '../types';

type SortKey = 'domain' | 'pages' | 'queued' | 'size' | 'errors' | 'last_crawl';
type SortDir = 'asc' | 'desc';
// 'indexing' is a synthetic filter — not a real archive.status value.
// The backend translates it to (status='done' AND text_indexing_started_at
// IS NOT NULL).
type StatusFilter = Archive['status'] | 'indexing';
const STATUSES: Archive['status'][] = ['pending', 'crawling', 'recrawling', 'degraded', 'retry_run', 'paused', 'terminated', 'done', 'error', 'deleting'];
const FILTER_OPTIONS: StatusFilter[] = [...STATUSES, 'indexing'];

function isSortKey(s: string | null): s is SortKey {
  return s === 'domain' || s === 'pages' || s === 'queued' || s === 'size' || s === 'errors' || s === 'last_crawl';
}

// "Active" = the worker is plausibly still going to make progress on
// this archive. Inactive sparklines render greyed so the eye lands on
// the live ones.
const ACTIVE_STATUSES = new Set<Archive['status']>(['pending', 'crawling', 'degraded', 'retry_run', 'recrawling']);
function isActiveStatus(s: Archive['status']): boolean {
  return ACTIVE_STATUSES.has(s);
}

/** "May 15 2026" — date only, no time, no comma. Keeps the Last-crawl
 *  column compact since exact times aren't useful at the list level
 *  (the per-archive page already shows full timestamps where it matters). */
function fmtShortDate(iso: string): string {
  const d = new Date(iso);
  const month = d.toLocaleString('en-US', { month: 'short' });
  return `${month} ${d.getDate()} ${d.getFullYear()}`;
}

function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${units[i]}`;
}

export default function Home() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [archives, setArchives] = useState<Archive[]>([]);
  // Meta is the corpus-wide info — total matching count + summed
  // pages/bytes/errors across every matching archive. The list itself
  // only shows the current page (50 rows), but the footer "Total" row
  // and the pager need numbers from the whole result set.
  const [meta, setMeta] = useState<ArchivesIndexMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const fetchSeq = useRef(0);

  // Filter / sort state lives in the URL so reload preserves it and the
  // user can share/bookmark a particular view. replace:true on every
  // mutation keeps history clean — back-button shouldn't step through
  // every filter toggle.
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const statusParam = searchParams.get('status') ?? '';
  const selectedStatuses = useMemo<Set<string>>(
    () => new Set(statusParam.split(',').filter(Boolean)),
    [statusParam],
  );
  const sortRaw = searchParams.get('sort');
  const sort: SortKey | '' = isSortKey(sortRaw) ? sortRaw : '';
  const dir: SortDir = (searchParams.get('dir') === 'asc' ? 'asc' : 'desc');
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') | 0);

  // Filter/sort changes reset to page 1. Page changes alone leave
  // other params untouched.
  function updateParam(name: string, value: string | null) {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === '') next.delete(name);
    else next.set(name, value);
    if (name !== 'page') next.delete('page');
    setSearchParams(next, { replace: true });
  }
  function goToPage(p: number) {
    const next = new URLSearchParams(searchParams);
    if (p <= 1) next.delete('page'); else next.set('page', String(p));
    setSearchParams(next, { replace: false });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  function setQ(v: string) { updateParam('q', v); }
  function toggleStatus(s: StatusFilter) {
    const next = new Set(selectedStatuses);
    if (next.has(s)) next.delete(s); else next.add(s);
    updateParam('status', Array.from(next).join(','));
  }
  function clearStatuses() { updateParam('status', null); }

  // Bulk-action selection. Per-archive-id set; cleared when filters
  // change so the user can't accidentally act on rows they're no
  // longer looking at.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkBusy, setBulkBusy] = useState<string | null>(null);

  const [seedUrl, setSeedUrl] = useState('');
  // Optional manual override for the site_type. '' = auto-detect at
  // crawl time. circuitmaker_forum forces login + the
  // circuitmaker_workspace handler (cross-domain ASP.NET auth on the
  // workspace.<root> sibling).
  const [siteType, setSiteType] = useState<'' | 'circuitmaker_forum'>('');
  const [requiresLogin, setRequiresLogin] = useState(false);
  const [loginHandler, setLoginHandler] = useState('memberpress');
  const [loginUsername, setLoginUsername] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const seq = ++fetchSeq.current;
    setLoading(true);
    // Filter/sort change drops the selection — acting on rows that
    // are no longer in view is footgun-prone.
    setSelectedIds(new Set());

    // Main list paints first — just SELECT archives + filter/sort/limit.
    // Everything else (queued counts, index progress, sparklines, tags)
    // hydrates in parallel afterwards. fetchSeq guards against a stale
    // hydration response landing after a newer filter change.
    api<{ data: Archive[]; meta: ArchivesIndexMeta }>('/archives', {
      query: { q, status: statusParam, sort, dir, page },
    })
      .then(({ data, meta: respMeta }) => {
        if (fetchSeq.current !== seq) return;
        setArchives(data);
        setMeta(respMeta);
        setLoading(false);

        if (data.length === 0) return;
        const ids = data.map((a) => a.id).join(',');

        // Fire all four hydration calls in parallel; each merges into
        // archives state as it returns. Order doesn't matter — each
        // populates a different property.
        api<{ data: Record<string, number | null> }>('/archives/queued-counts', { query: { ids } })
          .then(({ data: byId }) => {
            if (fetchSeq.current !== seq) return;
            setArchives((current) => current.map((a) => ({
              ...a,
              queued_count: byId[String(a.id)] ?? null,
            })));
          })
          .catch((e) => console.error('queued-counts hydrate failed', e));

        api<{ data: Record<string, { indexed: number; total: number } | null> }>('/archives/index-progress', { query: { ids } })
          .then(({ data: byId }) => {
            if (fetchSeq.current !== seq) return;
            setArchives((current) => current.map((a) => {
              const progress = byId[String(a.id)];
              return progress ? { ...a, text_index_progress: progress } : a;
            }));
          })
          .catch((e) => console.error('index-progress hydrate failed', e));

        api<{ data: Record<string, SparklinePoint[]> }>('/archives/sparklines', { query: { ids } })
          .then(({ data: byId }) => {
            if (fetchSeq.current !== seq) return;
            setArchives((current) => current.map((a) => ({
              ...a,
              sparkline: byId[String(a.id)] ?? [],
            })));
          })
          .catch((e) => console.error('sparklines hydrate failed', e));

        api<{ data: Record<string, ArchiveTag[]> }>('/archives/tags-batch', { query: { ids } })
          .then(({ data: byId }) => {
            if (fetchSeq.current !== seq) return;
            setArchives((current) => current.map((a) => ({
              ...a,
              tags: byId[String(a.id)] ?? [],
            })));
          })
          .catch((e) => console.error('tags hydrate failed', e));
      })
      .catch((e) => {
        if (fetchSeq.current !== seq) return;
        console.error(e);
        setLoading(false);
      });
  }, [q, statusParam, sort, dir, page, reloadKey]);

  function toggleSort(key: SortKey) {
    const next = new URLSearchParams(searchParams);
    if (sort === key) {
      next.set('dir', dir === 'asc' ? 'desc' : 'asc');
    } else {
      next.set('sort', key);
      next.set('dir', key === 'domain' ? 'asc' : 'desc');
    }
    setSearchParams(next, { replace: true });
  }
  function sortIndicator(key: SortKey) {
    if (sort !== key) return '';
    return dir === 'asc' ? ' ↑' : ' ↓';
  }
  const sortableHeader = (key: SortKey, label: string) => (
    <th onClick={() => toggleSort(key)} style={{ cursor: 'pointer', userSelect: 'none' }}>
      {label}{sortIndicator(key)}
    </th>
  );

  // circuitmaker_forum always requires the cross-domain workspace login;
  // surfacing the credential fields and locking the handler is simpler
  // than asking the operator to also remember to tick the box.
  const forcedLogin = siteType === 'circuitmaker_forum';
  const effectiveRequiresLogin = forcedLogin || requiresLogin;
  const effectiveHandler = forcedLogin ? 'circuitmaker_workspace' : loginHandler;

  function toggleSelected(id: number, checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  }
  function toggleSelectAll(checked: boolean) {
    setSelectedIds(checked ? new Set(archives.map((a) => a.id)) : new Set());
  }
  function clearSelection() {
    setSelectedIds(new Set());
  }

  /** One round-trip per bulk op via the dedicated bulk endpoint.
   *  The backend applies the state gate to every id atomically and
   *  returns {changed, skipped} so the alert reports honest counts
   *  (the old N-loop conflated "request returned 200" with "archive
   *  state changed"). */
  async function runBulk(
    label: string,
    request: () => Promise<{ data: { changed: number; skipped: number } }>,
  ): Promise<void> {
    if (selectedIds.size === 0) return;
    setBulkBusy(label);
    try {
      const { data } = await request();
      if (data.skipped > 0 && data.changed === 0) {
        window.alert(`${label}: nothing changed — all ${data.skipped} selected archives were already in a non-${label}able state.`);
      } else if (data.skipped > 0) {
        window.alert(`${label}: ${data.changed} changed, ${data.skipped} skipped (already in a non-${label}able state).`);
      }
    } catch (e) {
      console.error(`bulk ${label} failed:`, e);
      window.alert(`${label}: request failed. Check console for details.`);
    } finally {
      setBulkBusy(null);
      clearSelection();
      setReloadKey((k) => k + 1);
    }
  }

  async function bulkPause() {
    if (!window.confirm(`Pause ${selectedIds.size} archive(s)? Currently-active crawls will stop; queue items are kept (Resume picks up where it left off). Already-paused/done archives are no-ops.`)) return;
    await runBulk('pause', () => api('/archives/bulk/pause', { method: 'POST', body: { ids: Array.from(selectedIds) } }));
  }
  async function bulkResume() {
    if (!window.confirm(`Resume ${selectedIds.size} archive(s)? Only paused/degraded archives actually move; the rest are no-ops.`)) return;
    await runBulk('resume', () => api('/archives/bulk/resume', { method: 'POST', body: { ids: Array.from(selectedIds) } }));
  }
  async function bulkTerminate() {
    if (!window.confirm(`Terminate ${selectedIds.size} archive(s)? Pending queue items are deleted. Revivable via "Resume crawl" on the archive page.`)) return;
    await runBulk('terminate', () => api('/archives/bulk/terminate', { method: 'POST', body: { ids: Array.from(selectedIds) } }));
  }
  async function bulkDelete() {
    if (!window.confirm(`PERMANENTLY DELETE ${selectedIds.size} archive(s) and all their data? This cannot be undone.`)) return;
    if (!window.confirm('Really delete? Last chance.')) return;
    await runBulk('delete', () => api('/archives/bulk', { method: 'DELETE', body: { ids: Array.from(selectedIds) } }));
  }

  const [transferTarget, setTransferTarget] = useState<{ ids: number[] } | null>(null);

  async function onQueue(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const body: Record<string, unknown> = { seed_url: seedUrl };
    if (siteType) {
      body.site_type = siteType;
    }
    if (effectiveRequiresLogin) {
      body.requires_login = true;
      body.login_handler = effectiveHandler;
      body.login_username = loginUsername;
      body.login_password = loginPassword;
    }
    try {
      await api('/archives', { method: 'POST', body });
      setSeedUrl('');
      setSiteType('');
      setRequiresLogin(false);
      setLoginUsername('');
      setLoginPassword('');
      setReloadKey((k) => k + 1);
    } catch (err) {
      if (err instanceof ApiError) {
        const payload = err.payload as {
          errors?: Record<string, string[]>; message?: string;
        } | null;
        const first = payload?.errors && Object.values(payload.errors)[0]?.[0];
        setError(first ?? payload?.message ?? 'Queue failed');
      } else {
        setError('Queue failed');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      {isAdmin && (
        <div className="card">
          <h2>Queue an archive</h2>
          <form onSubmit={onQueue}>
            <div className="row">
              <label htmlFor="seed">Seed URL (e.g. https://example-forum.com/)</label>
              <input id="seed" type="url" placeholder="https://…"
                value={seedUrl} onChange={(e) => setSeedUrl(e.target.value)} required />
            </div>
            <div className="row">
              <label htmlFor="queue-site-type">Site type override (usually auto-detected)</label>
              <select
                id="queue-site-type"
                value={siteType}
                onChange={(e) => setSiteType(e.target.value as '' | 'circuitmaker_forum')}
              >
                <option value="">auto-detect</option>
                <option value="circuitmaker_forum">CircuitMaker forum (API-driven; requires Altium credentials)</option>
              </select>
            </div>
            {! forcedLogin && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0' }}>
                <input
                  type="checkbox"
                  checked={requiresLogin}
                  onChange={(e) => setRequiresLogin(e.target.checked)}
                />
                <span>This site requires login</span>
              </label>
            )}
            {effectiveRequiresLogin && (
              <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 8, alignItems: 'center', margin: '8px 0 12px' }}>
                {! forcedLogin && (
                  <>
                    <label htmlFor="queue-handler">Login type</label>
                    <select id="queue-handler" value={loginHandler} onChange={(e) => setLoginHandler(e.target.value)}>
                      <option value="memberpress">MemberPress (WordPress)</option>
                      <option value="xenforo">XenForo</option>
                      <option value="invision">Invision Community (IPS 4.x)</option>
                    </select>
                  </>
                )}
                {forcedLogin && (
                  <>
                    <span className="muted" style={{ fontSize: 12 }}>Login type</span>
                    <span className="muted" style={{ fontSize: 12 }}>Altium / CircuitMaker (cross-domain, automatic)</span>
                  </>
                )}

                <label htmlFor="queue-username">Username</label>
                <input id="queue-username" type="text" value={loginUsername} onChange={(e) => setLoginUsername(e.target.value)} autoComplete="off" required />

                <label htmlFor="queue-password">Password</label>
                <input id="queue-password" type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} autoComplete="off" required />
              </div>
            )}
            <button type="submit" disabled={submitting}>
              {submitting ? (effectiveRequiresLogin ? 'Verifying login & queueing…' : 'Queueing…') : (effectiveRequiresLogin ? 'Test login & queue' : 'Queue archive')}
            </button>
            {error && <div className="error">{error}</div>}
          </form>
        </div>
      )}

      <div className="card">
        <h2>Archives</h2>
        <div className="row">
          <input placeholder="Filter by domain…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', margin: '8px 0 12px' }}>
          {FILTER_OPTIONS.map((s) => (
            <FilterPill
              key={s}
              status={s}
              active={selectedStatuses.has(s)}
              onClick={() => toggleStatus(s)}
            />
          ))}
          {selectedStatuses.size > 0 && (
            <button
              type="button"
              onClick={clearStatuses}
              className="link-button"
              style={{ background: 'none', border: 0, color: '#06c', cursor: 'pointer', fontSize: 12, marginLeft: 4 }}
            >
              Clear ({selectedStatuses.size})
            </button>
          )}
        </div>
        {isAdmin && selectedIds.size > 0 && (
          <BulkActionBar
            count={selectedIds.size}
            busy={bulkBusy}
            onPause={bulkPause}
            onResume={bulkResume}
            onTerminate={bulkTerminate}
            onTransfer={() => setTransferTarget({ ids: Array.from(selectedIds) })}
            onDelete={bulkDelete}
            onClear={clearSelection}
          />
        )}
        {loading
          ? <p className="muted">Loading…</p>
          : archives.length === 0
          ? <p className="muted">{q || selectedStatuses.size > 0 ? 'No archives match.' : 'No archives yet. Queue one above.'}</p>
          : (
            <table>
              <thead>
                <tr>
                  {isAdmin && (
                    <th style={{ width: 28 }}>
                      <input
                        type="checkbox"
                        title={selectedIds.size === archives.length ? 'Clear selection' : 'Select all visible'}
                        checked={archives.length > 0 && selectedIds.size === archives.length}
                        // indeterminate isn't a JSX attribute; set via ref
                        ref={(el) => {
                          if (el) el.indeterminate = selectedIds.size > 0 && selectedIds.size < archives.length;
                        }}
                        onChange={(e) => toggleSelectAll(e.target.checked)}
                      />
                    </th>
                  )}
                  {sortableHeader('domain', 'Domain')}
                  <th>Status</th>
                  <th title="Full-text search index state for this archive">Search</th>
                  {sortableHeader('pages', 'Pages')}
                  {sortableHeader('queued', 'Queued')}
                  <th title="Pages (green) and queued (blue) over the last 24h">Trend</th>
                  {sortableHeader('size', 'Size')}
                  {sortableHeader('errors', 'Errors')}
                  {sortableHeader('last_crawl', 'Last crawl')}
                </tr>
              </thead>
              <tbody>
                {archives.map((a) => (
                  <tr key={a.id} style={selectedIds.has(a.id) ? { background: 'var(--bg-muted)' } : undefined}>
                    {isAdmin && (
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(a.id)}
                          onChange={(e) => toggleSelected(a.id, e.target.checked)}
                          aria-label={`Select ${a.domain}`}
                        />
                      </td>
                    )}
                    <td style={{ maxWidth: 320 }}>
                      <Link
                        to={`/archives/${a.id}`}
                        title={`${a.domain}${a.path_prefix}`}
                        style={{
                          display: 'inline-block',
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          verticalAlign: 'bottom',
                        }}
                      >{a.domain}{a.path_prefix}</Link>
                      {a.needs_local_worker && (
                        <HoverHint
                          hint="Some URLs are behind an anti-bot challenge (Cloudflare, CrowdSec, Anubis). Those URLs stay unfetched; the rest of the crawl proceeds normally. Open the archive page for details or to dismiss."
                          ariaLabel="Anti-bot challenge detected"
                        >
                          <span style={{ marginLeft: 6, color: 'var(--fg-warn)', cursor: 'help', fontSize: 13, verticalAlign: 'middle' }}>⚠</span>
                        </HoverHint>
                      )}
                      {a.tags && a.tags.length > 0 && <TagsCell tags={a.tags} />}
                    </td>
                    <td>
                      <span className={`status-pill ${a.status}`} title={a.last_error ?? ''}>{a.status}</span>
                    </td>
                    <td>
                      <SearchIndexCell archive={a} />
                    </td>
                    <td>{a.pages_fetched.toLocaleString()}</td>
                    <td className="muted">
                      {a.queued_count === undefined
                        ? <span style={{ opacity: 0.4 }}>…</span>
                        : a.queued_count == null
                          ? '—'
                          : a.queued_count.toLocaleString()}
                    </td>
                    <td>
                      {a.sparkline === undefined
                        ? <span className="muted" style={{ opacity: 0.4 }}>…</span>
                        : a.sparkline.length > 0
                          ? <Sparkline points={a.sparkline} muted={!isActiveStatus(a.status)} />
                          : <span className="muted">—</span>}
                    </td>
                    <td>{fmtBytes(a.bytes_fetched)}</td>
                    <td>{a.errors_count.toLocaleString()}</td>
                    <td className="muted" style={{ whiteSpace: 'nowrap' }}>
                      {a.last_crawled_at ? fmtShortDate(a.last_crawled_at) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 600, borderTop: '2px solid var(--border)' }}>
                  {isAdmin && <td />}
                  <td className="muted">
                    Total ({(meta?.total ?? 0).toLocaleString()})
                  </td>
                  <td /> <td /> {/* Status, Search */}
                  <td>{(meta?.totals.pages_fetched ?? 0).toLocaleString()}</td>
                  <td /> <td /> {/* Queued, Trend */}
                  <td>{fmtBytes(meta?.totals.bytes_fetched ?? 0)}</td>
                  <td>{(meta?.totals.errors_count ?? 0).toLocaleString()}</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          )}
          {meta && meta.last_page > 1 && (
            <ArchivesPager
              page={meta.page}
              lastPage={meta.last_page}
              perPage={meta.per_page}
              total={meta.total}
              onGo={goToPage}
            />
          )}
      </div>
      {transferTarget && (
        <TransferOwnerDialog
          archiveIds={transferTarget.ids}
          domain={`${transferTarget.ids.length} archive${transferTarget.ids.length === 1 ? '' : 's'}`}
          onClose={() => setTransferTarget(null)}
          onTransferred={() => {
            clearSelection();
            setReloadKey((k) => k + 1);
          }}
        />
      )}
    </>
  );
}

/** Sticky bar above the table when 1+ archives are checked. Buttons
 *  fire bulk operations that loop the per-archive endpoints; the
 *  endpoints themselves no-op when the archive isn't in an
 *  actionable state, so we don't pre-filter by status here. */
function BulkActionBar({
  count, busy, onPause, onResume, onTerminate, onTransfer, onDelete, onClear,
}: {
  count: number;
  busy: string | null;
  onPause: () => void;
  onResume: () => void;
  onTerminate: () => void;
  onTransfer: () => void;
  onDelete: () => void;
  onClear: () => void;
}) {
  const disabled = busy !== null;
  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
      padding: '8px 12px', marginBottom: 12,
      background: 'var(--bg-muted)', border: '1px solid var(--border-card)',
      borderRadius: 4, fontSize: 13,
    }}>
      <strong>{count} selected</strong>
      <span className="muted">·</span>
      <button type="button" onClick={onPause} disabled={disabled} className="secondary" style={{ padding: '2px 10px', fontSize: 12 }}>
        {busy === 'pause' ? 'Pausing…' : 'Pause'}
      </button>
      <button type="button" onClick={onResume} disabled={disabled} className="secondary" style={{ padding: '2px 10px', fontSize: 12 }}>
        {busy === 'resume' ? 'Resuming…' : 'Resume'}
      </button>
      <button type="button" onClick={onTerminate} disabled={disabled} className="secondary" style={{ padding: '2px 10px', fontSize: 12 }}>
        {busy === 'terminate' ? 'Terminating…' : 'Terminate'}
      </button>
      <button type="button" onClick={onTransfer} disabled={disabled} className="secondary" style={{ padding: '2px 10px', fontSize: 12 }}>
        Transfer
      </button>
      <button
        type="button"
        onClick={onDelete}
        disabled={disabled}
        className="secondary"
        style={{ padding: '2px 10px', fontSize: 12, color: 'var(--fg-error)', borderColor: 'var(--pill-border-stalled)' }}
      >
        {busy === 'delete' ? 'Deleting…' : 'Delete'}
      </button>
      <span style={{ flex: 1 }} />
      <button
        type="button"
        onClick={onClear}
        disabled={disabled}
        className="link-button"
        style={{ background: 'none', border: 0, color: 'var(--fg-link)', cursor: 'pointer', fontSize: 12, padding: 0 }}
      >
        Clear selection
      </button>
    </div>
  );
}

/** Reliable cross-browser hover popover. The native `title` attribute
 *  is inconsistent across browsers (long delay, sometimes never shows
 *  on small inline elements); React state + an absolutely-positioned
 *  div appears immediately. */
function HoverHint({ children, hint, ariaLabel }: {
  children: React.ReactNode;
  hint: string;
  ariaLabel?: string;
}) {
  const [hover, setHover] = useState(false);
  return (
    <span
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      tabIndex={0}
      aria-label={ariaLabel}
    >
      {children}
      {hover && (
        <span
          role="tooltip"
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: 4,
            padding: '6px 8px',
            background: 'var(--bg-tooltip)',
            border: '1px solid var(--border-tooltip)',
            borderRadius: 4,
            fontSize: 12,
            lineHeight: 1.4,
            width: 280,
            zIndex: 20,
            boxShadow: 'var(--shadow-tooltip)',
            pointerEvents: 'none',
            color: 'var(--fg)',
            fontWeight: 400,
            whiteSpace: 'normal',
            display: 'block',
          }}
        >
          {hint}
        </span>
      )}
    </span>
  );
}

/** Inline tag list cell. Caps at 3 visible pills; if there's a fourth,
 *  collapses the rest behind a "+N" pill that toggles to show them all.
 *  Tags themselves link to /tags/{name}. */
function TagsCell({ tags }: { tags: ArchiveTag[] }) {
  const [expanded, setExpanded] = useState(false);
  const CAP = 3;
  const showAll = expanded || tags.length <= CAP;
  const visible = showAll ? tags : tags.slice(0, CAP);
  const hidden = tags.length - visible.length;

  const pill: React.CSSProperties = {
    display: 'inline-block',
    marginLeft: 6,
    padding: '1px 8px',
    borderRadius: 999,
    background: 'var(--pill-bg-tag)',
    border: '1px solid var(--pill-border-tag)',
    color: 'var(--pill-fg-tag)',
    fontSize: 11,
    textDecoration: 'none',
    verticalAlign: 'baseline',
  };

  return (
    <>
      {visible.map((t) => (
        <Link key={t.id} to={`/tags/${encodeURIComponent(t.name)}`} style={pill}>
          {t.name}
        </Link>
      ))}
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          title={`Show ${hidden} more tag${hidden === 1 ? '' : 's'}`}
          className="link-button"
          style={{
            ...pill,
            background: 'var(--bg-muted)',
            borderColor: 'var(--border)',
            color: 'var(--fg-muted)',
            cursor: 'pointer',
          }}
        >
          +{hidden}
        </button>
      )}
    </>
  );
}

/** Compact "search index status" cell for the Home table.
 *  - while a done archive is indexing: blue "indexing X / Y" pill
 *  - once indexed: green "✓ indexed" pill
 *  - otherwise: muted dash so the column lines up tidily */
function SearchIndexCell({ archive }: { archive: Archive }) {
  if (archive.status === 'done') {
    const idx = indexerState(archive);
    if (idx.state === 'running') {
      return (
        <span
          title={`Search-index pass started ${new Date(idx.startedAt).toLocaleTimeString()}`}
          style={{ fontSize: 11, padding: '1px 6px', borderRadius: 999, background: 'var(--pill-bg-indexing)', border: '1px solid var(--pill-border-indexing)', color: 'var(--pill-fg-indexing)', whiteSpace: 'nowrap' }}
        >
          {archive.text_index_progress
            ? `indexing ${archive.text_index_progress.indexed.toLocaleString()} / ${archive.text_index_progress.total.toLocaleString()}`
            : 'indexing…'}
        </span>
      );
    }
    if (idx.state === 'stalled') {
      return (
        <span
          title={`Indexer subprocess presumed dead — no heartbeat for ${fmtElapsed(idx.staleForMs)}. Open the archive to restart.`}
          style={{ fontSize: 11, padding: '1px 6px', borderRadius: 999, background: 'var(--pill-bg-stalled)', border: '1px solid var(--pill-border-stalled)', color: 'var(--pill-fg-stalled)', whiteSpace: 'nowrap' }}
        >
          indexer stalled
        </span>
      );
    }
  }
  if (archive.text_indexed_at) {
    return (
      <span
        title={`Searchable — index built ${new Date(archive.text_indexed_at).toLocaleString()}`}
        style={{ fontSize: 11, padding: '1px 6px', borderRadius: 999, background: 'var(--pill-bg-indexed)', border: '1px solid var(--pill-border-indexed)', color: 'var(--pill-fg-indexed)', whiteSpace: 'nowrap' }}
      >
        ✓ indexed
      </span>
    );
  }
  return <span className="muted">—</span>;
}

/** Toggleable status filter pill. Active uses the status-pill colour
 *  (so the selected filter visually matches the badges in the rows it
 *  selects); inactive is a muted outline so the row reads as "not yet
 *  selected, click to add." */
function FilterPill({ status, active, onClick }: {
  status: StatusFilter;
  active: boolean;
  onClick: () => void;
}) {
  // Indexing isn't a real status-pill class, so it gets the active-state
  // colour treatment inline. Other statuses inherit from .status-pill.{name}.
  const indexingTone = { background: '#def', border: '1px solid #9bd', color: '#057' };
  const inactiveTone = { background: 'transparent', border: '1px dashed #ccc', color: '#666' };

  const styleBase: React.CSSProperties = {
    fontSize: 12,
    padding: '2px 10px',
    borderRadius: 999,
    cursor: 'pointer',
    fontWeight: 500,
    userSelect: 'none',
    lineHeight: '18px',
    fontFamily: 'inherit',
  };

  if (!active) {
    return (
      <button type="button" onClick={onClick} className="link-button" style={{ ...styleBase, ...inactiveTone }}>
        {status}
      </button>
    );
  }

  if (status === 'indexing') {
    return (
      <button type="button" onClick={onClick} className="link-button" style={{ ...styleBase, ...indexingTone, border: '1px solid #9bd' }}>
        {status}
      </button>
    );
  }

  // Real status — reuse the status-pill colour scheme. .status-pill itself
  // declares its own padding/border-radius, so override those inline so the
  // button shape matches the inactive pills.
  return (
    <button
      type="button"
      onClick={onClick}
      className={`status-pill ${status}`}
      style={{ ...styleBase, border: '1px solid transparent' }}
    >
      {status}
    </button>
  );
}

/** Corpus-wide stats returned alongside the page slice. The totals
 *  block reflects every matching archive, NOT just the rows on this
 *  page — same as the pager numbers. */
type ArchivesIndexMeta = {
  total: number;
  page: number;
  per_page: number;
  last_page: number;
  totals: {
    pages_fetched: number;
    bytes_fetched: number;
    errors_count: number;
  };
};

/** Compact pager — First/Prev/window/Next/Last with a count display.
 *  Same shape as the Search and Discovered pagers. */
function ArchivesPager({
  page, lastPage, perPage, total, onGo,
}: {
  page: number; lastPage: number; perPage: number; total: number;
  onGo: (p: number) => void;
}) {
  const windowSize = 2;
  const start = Math.max(2, page - windowSize);
  const end = Math.min(lastPage - 1, page + windowSize);
  const items: (number | '…')[] = [1];
  if (start > 2) items.push('…');
  for (let i = start; i <= end; i++) items.push(i);
  if (end < lastPage - 1) items.push('…');
  if (lastPage > 1) items.push(lastPage);

  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);

  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
      <span className="muted" style={{ fontSize: 13, marginRight: 8 }}>
        Showing {from.toLocaleString()}–{to.toLocaleString()} of {total.toLocaleString()}
      </span>
      <button type="button" className="secondary" disabled={page <= 1} onClick={() => onGo(1)} title="First page">« First</button>
      <button type="button" className="secondary" disabled={page <= 1} onClick={() => onGo(page - 1)}>Prev</button>
      {items.map((it, idx) =>
        it === '…' ? (
          <span key={`g${idx}`} className="muted" style={{ padding: '0 4px' }}>…</span>
        ) : (
          <button
            key={it}
            type="button"
            className={it === page ? '' : 'secondary'}
            onClick={() => onGo(it)}
            style={{ minWidth: 32 }}
          >
            {it}
          </button>
        ),
      )}
      <button type="button" className="secondary" disabled={page >= lastPage} onClick={() => onGo(page + 1)}>Next</button>
      <button type="button" className="secondary" disabled={page >= lastPage} onClick={() => onGo(lastPage)} title="Last page">Last »</button>
    </div>
  );
}
